"use client";
import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  Children,
  isValidElement,
} from "react";
import Image from "next/image";
import type { HeroProps } from "../types";
import AbandonedCartSuggestions from "@/components/AbandonedCartSuggestions";
import Breadcrumbs from "@/components/Breadcrumbs/Breadcrumbs";
import InternalNav from "@/components/InternalNav";
import useTruncatedElement from "@/hooks/useTrancatedElement";
import { ElementViewed } from "@/hooks/eventing/types/redventures.usertracking.v3.ElementViewed";
import { ElementType, PageNames } from "@/constants/tagular/main";
import { getPageType, hyphenateForTagular } from "@/utils/tagular/helpers";
import useElementEvents from "@/hooks/eventing/useElementEvents";
import { useInView } from "react-intersection-observer";
import useResourcePageSideRail from "@/monarch/rules/resourcePageSideRail/useResourcePageSideRail";
import { usePathname } from "next/navigation";
import useTranslations from "@/i18n/useTranslations";

// Internal component to render offers
const OfferItem = ({
  discount,
  label,
  href,
  containerClassName = "",
  discountClassName,
  labelClassName,
}: {
  discount: string;
  label: string;
  href: string;
  containerClassName?: string;
  discountClassName: string;
  labelClassName: string;
}) => (
  <div
    className={`flex flex-row lg:flex-col text-center items-center gap-4 lg:gap-0 text-white ${containerClassName}`}
  >
    <span className={discountClassName}>{discount}</span>
    <a href={href} className={labelClassName}>
      {label}
    </a>
  </div>
);

export default function NoImageHero({
  title,
  titleOverride,
  path,
  subnavItems,
  hasBreadcrumbs = true,
  children,
  location,
  html_id,
  text = "",
  hassiderail = "false",
  offers,
}: HeroProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const { ref: inViewRef, inView } = useInView();

  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      ref.current = node as HTMLDivElement | null;
      inViewRef(node);
    },
    [inViewRef],
  );

  const { isTruncated, isReadingMore, setIsReadingMore } =
    useTruncatedElement(ref);
  const [pageType, setPageType] = useState("unknown");

  const tExpand = useTranslations("ExpandableBlock");
  const tCommon = useTranslations("Common");
  const truncateText = isReadingMore
    ? tExpand("showLess")
    : tCommon("readMore");

  const webElement: ElementViewed["webElement"] = {
    elementType: ElementType.Entry,
    htmlId: html_id,
    location: location,
    text: text,
    position: "1",
    name: hyphenateForTagular(text),
  };

  const { elementViewed, elementClicked } = useElementEvents({ webElement });
  const { variant } = useResourcePageSideRail();
  const pathname = usePathname();

  useEffect(() => {
    // For now, only send ElementViewed/ElementClicked eventing data if it's from course landing shop page.
    const page = getPageType();
    if (page) {
      setPageType(page);
    }
    if (inView && pageType === PageNames.ShopPage) {
      elementViewed();
    }
  }, [pageType, inView]);

  const handleClick = () => {
    if (pageType === PageNames.ShopPage) {
      elementClicked();
    }
  };

  // Split children: DynamicByline goes outside truncatable area
  // Detect DynamicByline by checking for data-dynamic-byline attribute on wrapper span
  const childArray = Children.toArray(children);
  const isDynamicByline = (child: React.ReactNode): boolean => {
    if (!isValidElement(child)) return false;
    const props = child.props as Record<string, unknown>;
    return props["data-dynamic-byline"] === "true";
  };
  const bylineChildren = childArray.filter(isDynamicByline);

  // Filter out bylines and also any separator text (commas, whitespace) between them
  const isBylineSeparator = (child: React.ReactNode): boolean => {
    if (typeof child === "string") {
      // Check if it's only whitespace, commas, or a combination
      return /^[\s,]*$/.test(child);
    }
    return false;
  };

  const otherChildren =
    bylineChildren.length > 0
      ? childArray.filter(
          (child) => !isDynamicByline(child) && !isBylineSeparator(child),
        )
      : childArray;

  // Collect byline content including separators to render together
  const bylineContent =
    bylineChildren.length > 0
      ? childArray.filter(
          (child) => isDynamicByline(child) || isBylineSeparator(child),
        )
      : [];

  const offer1 = offers?.[0];
  const offer2 = offers?.[1];
  const hasOffer1 = !!offer1;
  const hasOffer2 = !!offer2;
  const hasOffers = hasOffer1 || hasOffer2;
  const showOffer1InCol2 = hasOffer1 && hasOffer2;
  const showOffer1InCol3 = hasOffer1 && !hasOffer2;
  const showOffer2InCol3 = hasOffer2;

  const titleBaseClasses =
    "text-white text-learn-hero-heading mb-6 font-extrabold tracking-tight";
  const titleClassName = hasOffers
    ? `${titleBaseClasses} lg:text-4xl lg:leading-learn-hero-line-height-large`
    : `${titleBaseClasses} lg:text-learn-hero-heading-large lg:leading-learn-hero-line-height-large`;

  const offerDiscountClassName =
    "font-black text-3xl xl:text-4xl text-[#BEF4E5]";
  const offerLabelClassName =
    "font-normal text-base md:text-md xl:text-base underline text-white hover:text-white";

  const containerClassName = hasOffers
    ? "mx-auto lg:container [&>*]:text-white relative z-10 grid grid-cols-1 lg:grid-cols-[4fr_1fr_1fr] gap-4 lg:gap-6"
    : "mx-auto lg:container [&>*]:text-white relative z-10";

  return (
    <>
      <div className="fullwidth relative overflow-hidden pb-12 pt-20 md:pt-16">
        <Image
          src="/bg-images/learn-page-header.webp"
          alt=""
          fill
          className="object-cover object-center -z-10 !my-0 !max-w-full !px-0"
          fetchPriority="high"
          loading="eager"
        />
        <div className={containerClassName}>
          {/* Column 1: Main content (2/3 width when there are offers) */}
          <div className={hasOffers ? "lg:col-span-1" : ""}>
            {hasBreadcrumbs && path && (
              <Breadcrumbs
                variant="light"
                path={path}
                pageName={titleOverride || title}
              />
            )}
            <h1 className={titleClassName}>{titleOverride || title}</h1>
            <div
              ref={setRefs}
              onClick={handleClick}
              className={`break-words [&>ul>li>p]:text-white [&>*]:text-white text-xl ${!isReadingMore && "line-clamp-2"}`}
            >
              {otherChildren}
            </div>

            {isTruncated && children && (
              <button onClick={() => setIsReadingMore((prev) => !prev)}>
                {truncateText}
              </button>
            )}

            {bylineContent.length > 0 && (
              <div className="mt-4 [&>*]:text-white">{bylineContent}</div>
            )}
          </div>

          {/* Column 2: Offer1 (only if there is Offer2) */}
          {hasOffers && (
            <div className="flex flex-col justify-center gap-4 lg:gap-0">
              {showOffer1InCol2 && offer1 && (
                <OfferItem
                  discount={offer1.discount}
                  label={offer1.label}
                  href={offer1.href}
                  discountClassName={offerDiscountClassName}
                  labelClassName={offerLabelClassName}
                />
              )}
            </div>
          )}

          {/* Column 3: Offer2 (if there is Offer1) or Offer1 (if there is no Offer2) */}
          {hasOffers && (
            <div className="flex flex-col justify-center gap-4 lg:gap-0">
              {showOffer2InCol3 && offer2 && (
                <OfferItem
                  discount={offer2.discount}
                  label={offer2.label}
                  href={offer2.href}
                  containerClassName="-mt-6 md:mt-0"
                  discountClassName={offerDiscountClassName}
                  labelClassName={offerLabelClassName}
                />
              )}
              {showOffer1InCol3 && offer1 && (
                <OfferItem
                  discount={offer1.discount}
                  label={offer1.label}
                  href={offer1.href}
                  discountClassName={offerDiscountClassName}
                  labelClassName={offerLabelClassName}
                />
              )}
            </div>
          )}
        </div>
      </div>
      {variant === "siderail" &&
      pathname?.includes("/resources") &&
      hassiderail === "true" ? (
        <></>
      ) : (
        <InternalNav
          variant="primary"
          data={subnavItems}
          location={location}
          html_id={html_id}
        />
      )}
      {location === "learn-header" && (
        <AbandonedCartSuggestions className="pt-12" />
      )}
    </>
  );
}
